/**
 * Returns the value of the provided property on the provided object.
 *
 * Use dot notation syntax to reference properties on nested objects.
 *
 * Eample usage:
 *
 * ```
 * const colors = {
 *   black: '#000',
 *   gray: {
 *     500: '#aaa',
 *     900: '#222',
 *   },
 * };
 *
 * get(colors, 'black');    // returns '#000'
 * get(colors, 'gray.900'); // returns '#222'
 * ```
 *
 * @param {object} obj The object to reference.
 * @param {string|number|(string|number)[]} property A property accessor or
 *                                                   array of accessors.
 * @param {*} [defaultValue] A fallback return value.
 * @returns {unknown} The referenced object property value, or the provided
 *                    defaultValue.
 */
export function get(obj, property, defaultValue) {
  let value = obj;
  let path = property;
  if (Array.isArray(path)) {
    path = path.join('.');
  }
  path = typeof path === 'string' ? path.split('.') : [path];
  for (let i = 0; i < path.length; i++) {
    value = value?.[path[i]];
  }
  return typeof value === 'undefined' ? defaultValue : value;
}

/**
 * Utiliy functions for creating theme-based media queries.
 *
 * Example usage:
 *
 * ```
 * const StyledSpan = styled.span`
 *   margin-block: 1rem;
 *
 *   ${media.min('md')} {
 *     margin-block: 2.5rem;
 *   }
 * `;
 * ```
 */
export const media = {
  /**
   * Returns a `min-width` `@media` selector for the provided size.
   *
   * @param {string} [size] The theme.breakpoints key or any <length> value
   *                        (e.g. '40em')
   * @returns {string} An `@media` at-rule selector or an empty string if no
   *                   size can be resolved provided.
   */
  min:
    (size) =>
    ({ theme }) => {
      const bp = get(theme, ['breakpoints', size], size);
      return bp ? `@media (min-width: ${bp})` : '';
    },
  /**
   * Returns a `max-width` `@media` selector for the provided size.
   *
   * @param {string} [size] The theme.breakpoints key or any <length> value
   *                        (e.g. '40em')
   * @returns {string} An `@media` at-rule selector or an empty string if no
   *                   size can be resolved provided.
   */
  max:
    (size) =>
    ({ theme }) => {
      const bp = get(theme, ['breakpoints', size], size);
      return bp ? `@media (max-width: calc(${bp} - 1px))` : '';
    },
  /**
   * Returns a `hover` `@media` selector.
   *
   * @returns {string} An `@media` at-rule selector for hover or an empty
   *                   string if no hover media query is supported.
   */
  hover: `@media (hover: hover)`,
};

/**
 * A utility for retrieving theme values.
 *
 * Example usage:
 *
 * ```
 * const StyledDiv = styled.div`
 *   width: ${theme('sizes.lg')};
 *   z-index: ${theme('zIndex.banner')};
 * `;
 * ```
 *
 * @param {string} key Any theme key (e.g. 'zIndex.popup').
 * @param {string} defaultValue A fallback value to use if the
 *                              specified value can't be retrieved.
 * @returns {function} A themed CSS function that returns the value for the
 *                     provided the theme key.
 */
export function theme(key, defaultValue) {
  return (props) => get(props.theme, key, defaultValue);
}

/**
 * Returns a color theme value specified by the provided key.
 *
 * @param {string} [key] The theme.colors key
 * @param {string|number} [defaultValue] A fallback value
 * @returns {function} A themed CSS function that returns the value for the
 *                     provided the color key.
 */
export function color(key, defaultValue) {
  return ({ theme }) => get(theme.colors, key, defaultValue);
}

/**
 * Returns a font theme value specified by the provided key.
 *
 * @param {string} [key] The theme.fonts key
 * @returns {function} A themed CSS function that returns the value for the
 *                     provided the font key.
 */
export function font(key) {
  return ({ theme }) => get(theme.fonts, key);
}

/**
 * A utility for declaring variants of styled components.
 *
 * Example usage:
 *
 * ```
 * const StyledLink = styled.a`
 *   ${variants({
 *     primary: css`
 *       color: blue;
 *     `,
 *     secondary: {
 *       default: css`
 *         color: inherit;
 *       `,
 *       light: css`
 *         color: white;
 *       `
 *     }
 *   })}
 * `;
 * ```
 *
 * @param {object} obj A object of variant names and styles.
 * @returns {function} A themed CSS function that returns the styles specified
 *                     by the component's variant or `$variant` prop.
 */
export function variants(obj) {
  return (props) => {
    const variant = props.$variant || props.variant;
    return get(obj, variant);
  };
}
